import { ALL_LOCATIONS, LOCATION_META, LOCATION_SECTIONS } from './country-data';
import {
  containsArabicScript,
  localizedLocationLabel,
  locationSearchText,
} from './city-names';
import { normalizeSearchText } from './country-names';
import type { SupportedLocale } from '../hooks/useLocale';
export {
  formatRelativeDate,
  humanizeJobValue,
  JOB_STATUS_COLORS,
  mapDateFilterValue,
} from './jobs-format';

export interface JobFilterOption {
  value: string;
  labelKey?: string;
  label?: string;
  /**
   * Set on curated location options. The canonical English `${city}, ${country}`
   * string stays the option `value` (it is what the API matches against job
   * location text); this marks it as localizable at render time.
   */
  locationValue?: string;
}

export interface JobFilterGroup {
  id: 'location' | 'workMode' | 'jobType' | 'source' | 'applyMode' | 'datePosted';
  labelKey: string;
  options: JobFilterOption[];
  popularCount?: number;
}

// Remote (index 0) + GCC (index 1) are the first two sections from buildLocationSections
const LOCATION_POPULAR_COUNT =
  (LOCATION_SECTIONS[0]?.data.length ?? 0) + (LOCATION_SECTIONS[1]?.data.length ?? 0);

export const JOB_FILTER_GROUPS: JobFilterGroup[] = [
  {
    id: 'location',
    labelKey: 'jobs.filters.location',
    options: ALL_LOCATIONS.map((location) =>
      // The legacy Remote sentinel is a work mode, not a place: it has a
      // translation key already, so it localizes through the normal path.
      location === 'Remote'
        ? { value: location, labelKey: 'jobs.filters.remote' }
        : { value: location, label: location, locationValue: location },
    ),
    popularCount: LOCATION_POPULAR_COUNT,
  },
  {
    id: 'workMode',
    labelKey: 'jobs.filters.workMode',
    options: [
      { value: 'onsite', labelKey: 'jobs.filters.onSite' },
      { value: 'hybrid', labelKey: 'jobs.filters.hybrid' },
      { value: 'remote', labelKey: 'jobs.filters.remote' },
    ],
  },
  {
    id: 'jobType',
    labelKey: 'jobs.filters.jobType',
    options: [
      { value: 'full-time', labelKey: 'jobs.filters.fullTime' },
      { value: 'part-time', labelKey: 'jobs.filters.partTime' },
      { value: 'contract', labelKey: 'jobs.filters.contract' },
      { value: 'internship', labelKey: 'jobs.filters.internship' },
    ],
  },
  {
    id: 'source',
    labelKey: 'jobs.filters.source',
    options: [
      { value: 'tabbio_native', labelKey: 'jobs.catalog.source.tabbioNative' },
      { value: 'partner_ats', labelKey: 'jobs.catalog.source.partnerAts' },
      { value: 'ats_workable', labelKey: 'jobs.catalog.source.workable' },
      { value: 'google_jobs_dataforseo', labelKey: 'jobs.catalog.source.googleJobs' },
    ],
  },
  {
    id: 'applyMode',
    labelKey: 'jobs.filters.applyMode',
    options: [
      { value: 'tabbio_native', labelKey: 'jobs.catalog.applyMode.tabbioNative' },
      { value: 'external_redirect', labelKey: 'jobs.catalog.applyMode.externalRedirect' },
      { value: 'partner_ats_managed', labelKey: 'jobs.catalog.applyMode.partnerAtsManaged' },
    ],
  },
  {
    id: 'datePosted',
    labelKey: 'jobs.filters.datePosted',
    options: [
      { value: 'today', labelKey: 'jobs.filters.today' },
      { value: 'thisWeek', labelKey: 'jobs.filters.thisWeek' },
      { value: 'thisMonth', labelKey: 'jobs.filters.thisMonth' },
    ],
  },
];

export type JobFeedSingleFilterId = Exclude<JobFilterGroup['id'], 'location'>;

export interface JobFeedFilters {
  location: string[];
  workMode: string | null;
  jobType: string | null;
  source: string | null;
  applyMode: string | null;
  datePosted: string | null;
}

export type JobFeedFilterValue = string | string[] | null;

export function emptyJobFeedFilters(): JobFeedFilters {
  return {
    location: [],
    workMode: null,
    jobType: null,
    source: null,
    applyMode: null,
    datePosted: null,
  };
}

export function toggleJobLocationFilter(
  locations: readonly string[],
  location: string,
) {
  const normalizedLocation = location.trim();
  if (!normalizedLocation) {
    return [...locations];
  }

  if (locations.includes(normalizedLocation)) {
    return locations.filter((item) => item !== normalizedLocation);
  }

  return [...locations, normalizedLocation];
}

/**
 * Curated picker values are stored as `City, Country` display names, while the
 * catalog API intentionally keeps city and broad-location filters separate.
 * Sending those labels through the comma-delimited `locations` query widens a
 * multi-city selection to their shared country. Resolve curated values to the
 * precise city channel and leave custom/country/Remote values broad.
 */
export function buildJobDiscoveryLocationFilters(
  selected: readonly string[],
): { locations: string[]; cities: string[] } {
  const locations: string[] = [];
  const cities: string[] = [];
  const seenLocations = new Set<string>();
  const seenCities = new Set<string>();

  for (const value of selected) {
    const normalizedValue = value.trim();
    if (!normalizedValue) continue;

    const city = LOCATION_META[normalizedValue]?.city.trim();
    const target = city ? cities : locations;
    const seen = city ? seenCities : seenLocations;
    const normalizedTarget = (city ?? normalizedValue).toLowerCase();

    if (seen.has(normalizedTarget)) continue;
    seen.add(normalizedTarget);
    target.push(city ?? normalizedValue);
  }

  return { locations, cities };
}

// Caps keep the serialized snapshot far under the page-context envelope's
// 2KB state limit (see @tabbio/utils page-context schema).
const PAGE_CONTEXT_QUERY_MAX = 120;
const PAGE_CONTEXT_MAX_LOCATIONS = 10;
const PAGE_CONTEXT_LOCATION_MAX = 80;

/**
 * Whitelisted snapshot of the active job-feed filters for the agent's page
 * context. Only known filter fields are copied (never arbitrary UI state),
 * empties are omitted, and free-text values are length-capped. Returns
 * `undefined` when nothing is active so the envelope stays minimal.
 */
export function serializeJobFeedFiltersForPageContext(
  filters: JobFeedFilters,
  searchQuery?: string,
): Record<string, unknown> | undefined {
  const state: Record<string, unknown> = {};

  const query = searchQuery?.trim();
  if (query) {
    state.query = query.slice(0, PAGE_CONTEXT_QUERY_MAX);
  }
  if (filters.location.length > 0) {
    state.locations = filters.location
      .slice(0, PAGE_CONTEXT_MAX_LOCATIONS)
      .map((location) => location.slice(0, PAGE_CONTEXT_LOCATION_MAX));
  }
  if (filters.workMode) state.workMode = filters.workMode;
  if (filters.jobType) state.jobType = filters.jobType;
  if (filters.source) state.source = filters.source;
  if (filters.applyMode) state.applyMode = filters.applyMode;
  if (filters.datePosted) state.datePosted = filters.datePosted;

  return Object.keys(state).length > 0 ? state : undefined;
}

export function countActiveJobFilters(filters: JobFeedFilters) {
  return (
    filters.location.length +
    (filters.workMode ? 1 : 0) +
    (filters.jobType ? 1 : 0) +
    (filters.source ? 1 : 0) +
    (filters.applyMode ? 1 : 0) +
    (filters.datePosted ? 1 : 0)
  );
}

export function hasActiveJobFilters(filters: JobFeedFilters) {
  return countActiveJobFilters(filters) > 0;
}

export function mapPreferenceWorkModes(values?: string[]) {
  return (values ?? [])
    .map((value) => {
      const normalized = value.toLowerCase();
      if (normalized === 'remote') return 'remote';
      if (normalized === 'hybrid') return 'hybrid';
      if (normalized === 'on-site' || normalized === 'onsite') return 'onsite';
      return null;
    })
    .filter((value): value is 'onsite' | 'hybrid' | 'remote' => Boolean(value));
}

export function mapPreferenceEmploymentTypes(values?: string[]) {
  return (values ?? [])
    .map((value) => {
      const normalized = value.toLowerCase();
      if (normalized === 'full-time') return 'full-time';
      if (normalized === 'part-time') return 'part-time';
      if (normalized === 'contract') return 'contract';
      if (normalized === 'internship') return 'internship';
      return null;
    })
    .filter(
      (value): value is 'full-time' | 'part-time' | 'contract' | 'internship' =>
        value !== null,
    );
}

export function seedJobFiltersFromPreferences(prefs?: {
  preferredLocations?: string[];
  locationTypes?: string[];
  employmentTypes?: string[];
}) {
  const filters = emptyJobFeedFilters();
  const preferredLocations = [
    ...new Set(
      (prefs?.preferredLocations ?? [])
        .map((location) => location.trim())
        .filter(Boolean),
    ),
  ];
  const preferredWorkMode = mapPreferenceWorkModes(prefs?.locationTypes)[0] ?? null;
  const preferredJobType = mapPreferenceEmploymentTypes(prefs?.employmentTypes)[0] ?? null;

  if (preferredLocations.length > 0) {
    filters.location = preferredLocations;
  }

  if (preferredWorkMode) {
    filters.workMode = preferredWorkMode;
  }

  if (preferredJobType) {
    filters.jobType = preferredJobType;
  }

  return filters;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function getJobFilterOptionLabel(
  option: JobFilterOption,
  t: Translate,
  locale: SupportedLocale = 'en',
) {
  if (option.locationValue) {
    return localizedLocationLabel(option.locationValue, locale);
  }

  if (option.labelKey) {
    return t(option.labelKey);
  }

  if (option.label) {
    return option.label;
  }

  return option.value;
}

/**
 * Everything a location option can be found by: its rendered label, its
 * canonical English value, and its Arabic alias. Typing in either language
 * matches the same chip whatever the UI locale is.
 */
function locationOptionHaystack(
  option: JobFilterOption,
  t: Translate,
  locale: SupportedLocale,
): string {
  const rendered = normalizeSearchText(getJobFilterOptionLabel(option, t, locale));
  if (!option.locationValue) return rendered;
  return `${rendered} ${locationSearchText(option.locationValue)}`;
}

export function jobLocationOptionMatches(
  option: JobFilterOption,
  query: string,
  t: Translate,
  locale: SupportedLocale = 'en',
): boolean {
  const needle = normalizeSearchText(query);
  if (!needle) return true;
  return locationOptionHaystack(option, t, locale).includes(needle);
}

export function findExactJobLocationOption(
  query: string,
  t: Translate,
  locale: SupportedLocale = 'en',
): JobFilterOption | undefined {
  const needle = normalizeSearchText(query);
  if (!needle) return undefined;
  const options =
    JOB_FILTER_GROUPS.find((group) => group.id === 'location')?.options ?? [];
  return options.find((option) =>
    [
      normalizeSearchText(getJobFilterOptionLabel(option, t, locale)),
      normalizeSearchText(option.value),
      option.locationValue
        ? normalizeSearchText(localizedLocationLabel(option.locationValue, 'ar'))
        : '',
    ]
      .filter(Boolean)
      .includes(needle),
  );
}

/**
 * Whether free text typed into the location filter can be used as a filter
 * value verbatim.
 *
 * Job location text is stored in English, so an Arabic free-text value would
 * produce a chip that silently matches zero jobs. Arabic input only ever
 * reaches the filter through a curated option, which carries the English value.
 */
export function canUseCustomJobLocation(query: string): boolean {
  return query.trim().length > 0 && !containsArabicScript(query);
}
